import { Search, X } from 'lucide-react';

import { t } from '../../../lib/i18n';
import { type ClientFilterType, type SortOption } from '../types';
import { cn } from '../utils';

interface FilterCounts {
  all: number;
  verified: number;
  unverified: number;
  activeBids: number;
}

interface UserHeaderControlsProps {
  shown: number;
  total: number;
  sort: SortOption;
  onSortChange: (value: SortOption) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filterType: ClientFilterType;
  onFilterChange: (filter: ClientFilterType) => void;
  filterCounts: FilterCounts;
}

/**
 * Search + filter + sort strip of the «Клієнти» screen.
 *
 * The artboard draws the filters twice: a segmented control on desktop and a
 * horizontally scrolling pill row on mobile. They are the same four filters —
 * the shape changes because a 390px viewport has no room for a segment bar.
 *
 * Sort has no counterpart on the artboard; it is kept because the list has had
 * it since the port and dropping a control is a regression, not a redesign. It
 * is rendered as a bare mono select so it reads as a caption, not a fifth chip.
 */
export function UserHeaderControls({
  shown,
  total,
  sort,
  onSortChange,
  searchTerm,
  onSearchChange,
  filterType,
  onFilterChange,
  filterCounts,
}: UserHeaderControlsProps) {
  const filters: {
    value: ClientFilterType;
    label: string;
    short: string;
    count: number;
  }[] = (['all', 'activeBids', 'unverified', 'verified'] as const).map((value) => ({
    value,
    label: t(`clients.list.filters.${value}`),
    // The mobile artboard writes these shorter — «Активні», not «З активними
    // ставками» — because four full labels do not fit a 390px scroll row.
    short: t(`clients.list.filtersShort.${value}`),
    count: filterCounts[value],
  }));

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:gap-2">
      {/* ── Search ──────────────────────────────────────────────────────── */}
      <div className="flex h-11 items-center gap-2.5 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 lg:h-10 lg:max-w-[460px] lg:flex-[1_1_260px]">
        <Search size={16} strokeWidth={1.75} className="shrink-0 text-cf-ink-subtle" />
        <input
          type="text"
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t('clients.list.search')}
          aria-label={t('clients.list.search')}
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-cf-ink-subtle"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            aria-label={t('clients.list.clearSearch')}
            className="shrink-0 text-cf-ink-subtle hover:text-cf-ink"
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        )}
      </div>

      {/* ── Filters, mobile: scrolling pills ────────────────────────────── */}
      <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 lg:hidden">
        {filters.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => onFilterChange(filter.value)}
            className={cn(
              'h-9 shrink-0 rounded-full border px-3 text-[13px] font-semibold whitespace-nowrap',
              filterType === filter.value
                ? 'border-cf-brand bg-cf-brand text-white'
                : 'border-cf-line bg-cf-surface text-cf-ink',
            )}
          >
            {filter.short} · {filter.count}
          </button>
        ))}
        <SortSelect sort={sort} onSortChange={onSortChange} className="shrink-0" />
      </div>

      {/* ── Filters, desktop: segmented control ─────────────────────────── */}
      <div role="tablist" className="hidden gap-0.5 rounded-cf-lg bg-cf-fill p-[3px] lg:flex">
        {filters.map((filter) => (
          <button
            key={filter.value}
            type="button"
            role="tab"
            aria-selected={filterType === filter.value}
            onClick={() => onFilterChange(filter.value)}
            className={cn(
              'h-[34px] rounded-cf-md px-3 text-[13px] font-semibold whitespace-nowrap',
              filterType === filter.value
                ? 'bg-cf-surface text-cf-ink'
                : 'bg-transparent text-cf-ink-muted',
            )}
          >
            {filter.label}{' '}
            <span className="font-mono text-[11px] font-medium opacity-70">{filter.count}</span>
          </button>
        ))}
      </div>

      {/* ── Sort + the count line (desktop) ─────────────────────────────── */}
      <div className="hidden items-center gap-3 lg:ml-auto lg:flex">
        <SortSelect sort={sort} onSortChange={onSortChange} />
        <span className="font-mono text-[11px] leading-[14px] tracking-[0.08em] text-cf-ink-subtle uppercase">
          {t('clients.list.shown', { shown, total })}
        </span>
      </div>
    </div>
  );
}

/**
 * Sorting, as a caption-sized select. It rides inside the filter row in both
 * viewports so it costs no vertical band of its own — on mobile a row of its
 * own would push the first client card a whole line down the screen.
 */
function SortSelect({
  sort,
  onSortChange,
  className = '',
}: {
  sort: SortOption;
  onSortChange: (value: SortOption) => void;
  className?: string;
}) {
  return (
    <select
      value={sort}
      onChange={(event) => onSortChange(event.target.value as SortOption)}
      aria-label={t('clients.list.sortLabel')}
      className={`h-9 rounded-full border border-cf-line bg-cf-surface px-3 font-mono text-[11px] tracking-[0.04em] text-cf-ink-muted lg:h-[34px] lg:rounded-cf-md lg:px-2 ${className}`}
    >
      <option value="">{t('clients.sort.default')}</option>
      <option value="name:asc">{t('clients.sort.nameAsc')}</option>
      <option value="name:desc">{t('clients.sort.nameDesc')}</option>
      <option value="createdAt:desc">{t('clients.sort.newestFirst')}</option>
      <option value="createdAt:asc">{t('clients.sort.oldestFirst')}</option>
    </select>
  );
}
